// node_modules
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { faShoppingCart, faCogs, faFlask, faAtom, faPencilAlt, faSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Types
import { TTechnologyTrlDTO } from "Types";
// styles
import styles from "./technologyRequirementsTrlLevel.module.scss";
// Components
import { FindestTextBox } from "Components";
// Controllers
import { TechnologyControllerSingleton } from "Controllers";
// Contexts
import { CaseContext } from "Stores";
// Enums
import { CaseActionTypeEnum, ToastTypeEnum } from "Enums";
// Interfaces
import { ISetCaseConceptTechnologyListAction } from "Interfaces";
// Contexts
import { WebsocketContext } from "Providers";
// Constants
import { HubConstants } from "Constants";
// Helpers
import { ToastHelperSingleton } from "Helpers";

type TTechnologyRequirementsTrlLevelProps = {
    technologyId: number,
    technologyReadinessLevel: TTechnologyTrlDTO,
    isEditable?: boolean,
    resourceName?: string
};

export const TechnologyRequirementsTrlLevel: FC<TTechnologyRequirementsTrlLevelProps> = ({ technologyId, technologyReadinessLevel, isEditable, resourceName }: TTechnologyRequirementsTrlLevelProps) => {
    // Get websocket context
    const { webSocketController } = useContext(WebsocketContext);
    
    // Logic
    const getTrlWidth = useCallback((): string => {
        let trlWidth = "0px";
        if (technologyReadinessLevel.technologyReadinessLevel && !technologyReadinessLevel.isUserAdded) {
            trlWidth = "3%";
        } else if (technologyReadinessLevel.minimumTrl && technologyReadinessLevel.maximumTrl) {
            const trlPercentage = (technologyReadinessLevel.maximumTrl - technologyReadinessLevel.minimumTrl) * 12.5;
            trlWidth = `${trlPercentage}%`;
        }
        return trlWidth;
    }, [technologyReadinessLevel.isUserAdded, technologyReadinessLevel.maximumTrl, technologyReadinessLevel.minimumTrl, technologyReadinessLevel.technologyReadinessLevel]);

    const getTrlLeft = useCallback((): string => {
        let trlLeft = "0px";
        if (technologyReadinessLevel.technologyReadinessLevel && !technologyReadinessLevel.isUserAdded) {
            let trlPercentage = (9 - technologyReadinessLevel.technologyReadinessLevel) * 12.5;
            if (trlPercentage > 97) {
                trlPercentage = 97;
            } else if (trlPercentage > 0) {
                trlPercentage = trlPercentage - 1.5;
            }
            trlLeft = `${trlPercentage  }%`;
        } else if (technologyReadinessLevel.minimumTrl && technologyReadinessLevel.maximumTrl) {
            const trlPercentage = (9 - technologyReadinessLevel.maximumTrl) * 12.5;
            trlLeft = `${trlPercentage  }%`;
        }
        return trlLeft;
    }, [technologyReadinessLevel.isUserAdded, technologyReadinessLevel.maximumTrl, technologyReadinessLevel.minimumTrl, technologyReadinessLevel.technologyReadinessLevel]);

    // State
    const [editedStartTRL, setEditedStartTRL] = useState<string | undefined>(undefined);
    const [editedEndTRL, setEditedEndTRL] = useState<string | undefined>(undefined);
    const [isEditing, setIsEditing] = useState(false);
    const [trlWidth, setTrlWidth] = useState(getTrlWidth());
    const [trlLeft, setTrlLeft] = useState(getTrlLeft());

    // Contexts
    const { caseState, caseDispatch } = useContext(CaseContext);

    // Logic
    const updateCaseState = useCallback((newStartTrl?: number, newEndTrl?: number) => {
        if(!caseState.conceptTechnologyList) return;
        const currentConceptTechnologyList = caseState.conceptTechnologyList;
        for(const concept of currentConceptTechnologyList.concepts) {
            for(const technology of concept.technologies) {
                if(technology.id === technologyId) {
                    if(technology.trl) {
                        technology.trl.isUserAdded = true;
                    } else {
                        technology.trl = { isUserAdded: true } as TTechnologyTrlDTO;
                    }
                    
                    if(newStartTrl) {
                        technology.trl.minimumTrl = newStartTrl;
                    }
                    if(newEndTrl) {
                        technology.trl.maximumTrl = newEndTrl;
                    }
                    break;
                }
            }
        }
        caseDispatch({ type: CaseActionTypeEnum.SetCaseConceptTechnologyList, caseConceptTechnologyList: currentConceptTechnologyList } as ISetCaseConceptTechnologyListAction);
    }, [caseDispatch, caseState.conceptTechnologyList, technologyId]);
    
    const onChangeStartTrl = useCallback(async (text: string) => {
        if (!text) return;
        const convertedText = parseInt(text);
        let newStartTrl: number | undefined = isNaN(convertedText) ? undefined : convertedText;
        if(newStartTrl) {
            if(newStartTrl <= 0) newStartTrl = 1;
            if(newStartTrl >= 9) newStartTrl = 9;
        }
        technologyReadinessLevel.minimumTrl = newStartTrl;

        setEditedStartTRL(undefined);
        const isSuccess = await TechnologyControllerSingleton.updateTechnologyTrl(technologyId, true, technologyReadinessLevel);
        if(isSuccess) {
            setTrlLeft(getTrlLeft());
            setTrlWidth(getTrlWidth());
            updateCaseState(newStartTrl, undefined);
        }
    }, [technologyReadinessLevel, technologyId, getTrlLeft, getTrlWidth, updateCaseState]);

    const onChangeEndTrl = useCallback(async (text: string) => {
        if (!text) return;
        const convertedText = parseInt(text);
        let newEndTrl: number | undefined = isNaN(convertedText) ? undefined : convertedText;
        if(newEndTrl) {
            if(newEndTrl <= 0) newEndTrl = 1;
            if(newEndTrl >= 9) newEndTrl = 9;
        }
        technologyReadinessLevel.maximumTrl = newEndTrl;

        setEditedEndTRL(undefined);
        const isSuccess = await TechnologyControllerSingleton.updateTechnologyTrl(technologyId, true, technologyReadinessLevel);
        if(isSuccess) {
            setTrlLeft(getTrlLeft());
            setTrlWidth(getTrlWidth());
            updateCaseState(undefined, newEndTrl);
        }
    }, [getTrlLeft, getTrlWidth, updateCaseState, technologyId, technologyReadinessLevel]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (!editedEndTRL) return;

            const newEndTRL = editedEndTRL.trim();

            if (!newEndTRL) {
                if (technologyReadinessLevel && technologyReadinessLevel.maximumTrl) {
                    setEditedEndTRL(technologyReadinessLevel.maximumTrl.toString());
                } else {
                    setEditedEndTRL(undefined);
                }
                return;
            }
            
            if (technologyReadinessLevel && 
                technologyReadinessLevel.maximumTrl &&
                newEndTRL.trim().toLowerCase() === technologyReadinessLevel.maximumTrl.toString().trim().toLowerCase()) return;
            
            onChangeEndTrl(newEndTRL);
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [editedEndTRL, onChangeEndTrl, technologyReadinessLevel]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (!editedStartTRL) return;

            const newStartTRL = editedStartTRL.trim();

            if (!newStartTRL) {
                if (technologyReadinessLevel && technologyReadinessLevel.minimumTrl) {
                    setEditedStartTRL(technologyReadinessLevel.minimumTrl.toString());
                } else {
                    setEditedStartTRL(undefined);
                }
                return;
            }
            
            if (technologyReadinessLevel && 
                technologyReadinessLevel.minimumTrl &&
                newStartTRL.trim().toLowerCase() === technologyReadinessLevel.minimumTrl.toString().trim().toLowerCase()) return;
            
            onChangeStartTrl(newStartTRL);
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [editedStartTRL, onChangeStartTrl, technologyReadinessLevel]);

    const receivedEditStatusChange = (newIsEditingValue: boolean) => {
        setIsEditing(newIsEditingValue);
    };

    const receivedAlreadyBeingEdited = () => {
        ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Somebody is already editing this field.");
    };

    
    useEffect(() => {
        if(isEditable && resourceName) {
            webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_EDIT_STATUS_CHANGE_PREFIX}${resourceName}`, receivedEditStatusChange);
            webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${resourceName}`, receivedAlreadyBeingEdited);

            // on clean up time, send stop claiming edit message to prevent claiming lock 
            // and remove handlers
            return () => {
                webSocketController.invokeFunction(HubConstants.STOP_EDITING_DELIVERY_RESOURCE, resourceName);
                webSocketController.removeHandler(`${HubConstants.DELIVERY_RESOURCE_EDIT_STATUS_CHANGE_PREFIX}${resourceName}`, receivedEditStatusChange);
                webSocketController.removeHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${resourceName}`, receivedAlreadyBeingEdited);
            };
        }
    }, [isEditable, resourceName, webSocketController]);

    const changeIsEditing = (doStartEditing: boolean) => {
        if(resourceName) {
            if(doStartEditing) {
                webSocketController.invokeFunction(HubConstants.START_EDITING_DELIVERY_RESOURCE, resourceName);
            } else {
                webSocketController.invokeFunction(HubConstants.STOP_EDITING_DELIVERY_RESOURCE, resourceName);
            }
        } else {
            setIsEditing(doStartEditing);
        }
    };

    return (
        <div className={styles.trlContainer}>
            <div className={styles.levelContainer}>
                <div className={styles.trlTheory}></div>
                <div className={styles.trlLab}></div>
                <div className={styles.trlPilot}></div>
                <div className={styles.trlCommercial}></div>
                <div className={styles.trlActual} style={{ width: trlWidth, left: trlLeft }} ></div>
            </div>
            <div className={styles.iconContainer}>
                <div>
                    <FontAwesomeIcon icon={faShoppingCart} />
                    <span>Commercial</span>
                </div>
                <div>
                    <FontAwesomeIcon icon={faCogs} />
                    <span>Large scale</span>
                </div>
                <div>
                    <FontAwesomeIcon icon={faFlask} />
                    <span>Small scale</span>
                </div>
                <div>
                    <FontAwesomeIcon icon={faAtom} />
                    <span>Theoretical</span>
                </div>
            </div>
            <div>
                {isEditable && !isEditing ?
                    <button className={[styles.changeContentButton, styles.edit].join(" ")} title={"Edit mode"} onClick={() => { changeIsEditing(true); }}>
                        <FontAwesomeIcon icon={faPencilAlt} />
                    </button> : null}
                {isEditing ?
                    <button className={[styles.changeContentButton, styles.save].join(" ")} title={"Exit edit mode"} onClick={() => { changeIsEditing(false); }}>
                        <FontAwesomeIcon icon={faPencilAlt} />
                        <FontAwesomeIcon className={styles.slash} icon={faSlash} />
                        <FontAwesomeIcon className={styles.slashBottom} icon={faSlash} />
                    </button>
                    : null}
            </div>
            {isEditing ?
                <div className={styles.changeTrlLevel}>
                    <FindestTextBox value={technologyReadinessLevel.minimumTrl?.toString()} onChange={setEditedStartTRL} placeholder={"Start TRL"} />
                    <span>-</span> 
                    <FindestTextBox value={technologyReadinessLevel.maximumTrl?.toString()} onChange={setEditedEndTRL} placeholder={"End TRL"} />
                </div>
                : null}
        </div>
    );
};